/**
 * 💰 AssignSalariesModal Component
 * Fecha: 30 de octubre de 2025
 *
 * Modal para que Admin RH asigne rangos salariales a una solicitud pre-aprobada.
 * Estado requerido: pre_aprobada → en_revision_salarios
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useEffect } from "react";
import { X, DollarSign, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { apiService, type JobRequest } from '@/services/api';
import { useJobRequestWorkflow } from '@/hooks/useJobRequestWorkflow';
import {
  formatJobRequestArea,
  formatJobRequestContractType,
  formatJobRequestProfile,
} from '@/features/rh/lib/jobRequestDisplay';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface AssignSalariesModalProps {
  jobRequest: JobRequest;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updated: JobRequest) => void;
}

const AssignSalariesModal: React.FC<AssignSalariesModalProps> = ({
  jobRequest,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [salarioMinimo, setSalarioMinimo] = useState("");
  const [salarioMaximo, setSalarioMaximo] = useState("");
  const [prestaciones, setPrestaciones] = useState("");
  const [comentarios, setComentarios] = useState("");
  const [recommendedMinRaw, setRecommendedMinRaw] = useState<unknown>(null);
  const [recommendedMaxRaw, setRecommendedMaxRaw] = useState<unknown>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const { assignSalaries, loading, error } = useJobRequestWorkflow();

  // Abrir con campos salariales vacíos; el valor previo solo se muestra como sugerencia
  useEffect(() => {
    if (isOpen && jobRequest) {
      setSalarioMinimo("");
      setSalarioMaximo("");
      setRecommendedMinRaw((jobRequest as any)?.salarioMinimo ?? (jobRequest as any)?.salario_minimo ?? null);
      setRecommendedMaxRaw((jobRequest as any)?.salarioMaximo ?? (jobRequest as any)?.salario_maximo ?? null);
      if (jobRequest.prestaciones) {
        setPrestaciones(jobRequest.prestaciones);
      }
      setValidationErrors({});
    }
  }, [isOpen, jobRequest]);

  // Reforzar sugerencias desde detalle para cubrir diferencias de payload en listados.
  useEffect(() => {
    if (!isOpen || !jobRequest?.id) return;

    let mounted = true;
    const loadJobRequestDetail = async () => {
      try {
        const response = await apiService.getJobRequestById(String(jobRequest.id));
        const detail = (response as any)?.data ?? response;
        if (!mounted || !detail) return;

        setRecommendedMinRaw(
          (detail as any)?.salarioMinimo ?? (detail as any)?.salario_minimo ?? null,
        );
        setRecommendedMaxRaw(
          (detail as any)?.salarioMaximo ?? (detail as any)?.salario_maximo ?? null,
        );
      } catch {
        // Si falla, mantenemos las sugerencias del listado sin bloquear el modal.
      }
    };

    loadJobRequestDetail();
    return () => {
      mounted = false;
    };
  }, [isOpen, jobRequest?.id]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const minSalary = parseFloat(salarioMinimo);
    const maxSalary = parseFloat(salarioMaximo);

    if (!salarioMinimo || isNaN(minSalary) || minSalary <= 0) {
      errors.salarioMinimo = "Ingrese un salario mínimo válido";
    }

    if (!salarioMaximo || isNaN(maxSalary) || maxSalary <= 0) {
      errors.salarioMaximo = "Ingrese un salario máximo válido";
    }

    if (minSalary > 0 && maxSalary > 0 && maxSalary < minSalary) {
      errors.salarioMaximo =
        "El salario máximo debe ser mayor o igual al mínimo";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await assignSalaries(jobRequest.id, {
      salarioMinimo: parseFloat(salarioMinimo),
      salarioMaximo: parseFloat(salarioMaximo),
      prestaciones: prestaciones.trim() || undefined,
      comentariosRH: comentarios.trim() || undefined,
    });

    if (result) {
      toast.success("Rangos salariales asignados exitosamente.");
      onSuccess(result);
      handleClose();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setSalarioMinimo("");
      setSalarioMaximo("");
      setPrestaciones("");
      setComentarios("");
      setValidationErrors({});
    }
  };

  const normalizeSalaryValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const cleaned = value.replace(/[^\d.-]/g, "");
      const num = parseFloat(cleaned);
      return Number.isNaN(num) ? null : num;
    }
    return null;
  };

  const formatCurrency = (value: string): string => {
    const num = normalizeSalaryValue(value);
    if (num === null) return "";
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(num);
  };

  const formatSuggestedCurrency = (value: unknown): string => {
    const num = normalizeSalaryValue(value);
    if (num === null) return "";
    if (isNaN(num)) return "";
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(num);
  };

  const suggestedMin = formatSuggestedCurrency(recommendedMinRaw);
  const suggestedMax = formatSuggestedCurrency(recommendedMaxRaw);

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Asignar rangos salariales
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {/* Información de la solicitud */}
            <div>
              <h3 className="font-medium text-black mb-2">
                {jobRequest.puestoSolicitar}
              </h3>
              <div className="text-sm text-[#1E1E1F] space-y-1">
                <p>
                  <strong>Departamento/Área:</strong>{" "}
                  {formatJobRequestArea(
                    jobRequest.area?.nombre || jobRequest.areaVacante,
                  )}
                </p>
                <p>
                  <strong>Tipo:</strong>{" "}
                  {formatJobRequestProfile(jobRequest.tipoPerfil)}
                </p>
                <p>
                  <strong>Tipo de contrato:</strong>{" "}
                  {formatJobRequestContractType(jobRequest.tipoContrato)}
                </p>
                {jobRequest.añosExperiencia !== undefined && (
                  <p>
                    <strong>Experiencia:</strong> {jobRequest.añosExperiencia}{" "}
                    años
                  </p>
                )}
              </div>
            </div>

            {/* Información */}
            <div>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-black">
                  <p className="font-medium mb-1">Importante:</p>
                  <p>
                    Estos rangos salariales serán revisados por el Gerente antes de la aprobación final.
                  </p>
                </div>
              </div>
            </div>

            {/* Rangos salariales */}
            <div className="grid grid-cols-2 gap-4">
              {/* Salario Mínimo */}
              <div>
                <label
                  htmlFor="salarioMinimo"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Salario mínimo (MXN) *
                </label>
                <input
                  type="number"
                  id="salarioMinimo"
                  value={salarioMinimo}
                  onChange={(e) => {
                    setSalarioMinimo(e.target.value);
                    setValidationErrors((prev) => ({
                      ...prev,
                      salarioMinimo: "",
                    }));
                  }}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                    validationErrors.salarioMinimo
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-indigo-500"
                  }`}
                  placeholder="00.00"
                  disabled={loading}
                />
                {validationErrors.salarioMinimo && (
                  <p className="mt-1 text-xs text-red-600">
                    {validationErrors.salarioMinimo}
                  </p>
                )}
                {salarioMinimo && !validationErrors.salarioMinimo && (
                  <p className="mt-1 text-xs text-gray-500">
                    {formatCurrency(salarioMinimo)}
                  </p>
                )}
                {!salarioMinimo && (
                  <p className="mt-1 text-xs text-gray-500">
                    Rango recomendado: {suggestedMin || "No especificado"}
                  </p>
                )}
              </div>

              {/* Salario Máximo */}
              <div>
                <label
                  htmlFor="salarioMaximo"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Salario máximo (MXN) *
                </label>
                <input
                  type="number"
                  id="salarioMaximo"
                  value={salarioMaximo}
                  onChange={(e) => {
                    setSalarioMaximo(e.target.value);
                    setValidationErrors((prev) => ({
                      ...prev,
                      salarioMaximo: "",
                    }));
                  }}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                    validationErrors.salarioMaximo
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-indigo-500"
                  }`}
                  placeholder="00.00"
                  disabled={loading}
                />
                {validationErrors.salarioMaximo && (
                  <p className="mt-1 text-xs text-red-600">
                    {validationErrors.salarioMaximo}
                  </p>
                )}
                {salarioMaximo && !validationErrors.salarioMaximo && (
                  <p className="mt-1 text-xs text-gray-500">
                    {formatCurrency(salarioMaximo)}
                  </p>
                )}
                {!salarioMaximo && (
                  <p className="mt-1 text-xs text-gray-500">
                    Rango recomendado: {suggestedMax || "No especificado"}
                  </p>
                )}
              </div>
            </div>

            {/* Prestaciones */}
            <div>
              <label
                htmlFor="prestaciones"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Prestaciones
              </label>
              <textarea
                id="prestaciones"
                value={prestaciones}
                onChange={(e) => setPrestaciones(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ej: Seguro de gastos médicos mayores, vales de despensa, aguinaldo..."
                disabled={loading}
              />
            </div>

            {/* Comentarios */}
            <div>
              <label
                htmlFor="comentarios"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Comentarios (Opcional)
              </label>
              <textarea
                id="comentarios"
                value={comentarios}
                onChange={(e) => setComentarios(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Notas sobre la asignación de salarios..."
                disabled={loading}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 bg-gray-50 sticky bottom-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={estiloDeBoton('primario')}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Asignando...</span>
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4" />
                  <span>Asignar salarios</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  );
};

export default AssignSalariesModal;
